import React, { useState, useEffect } from "react";
import { Breadcrumb, Tabs } from "@material-tailwind/react";
import ProjectDetailsContent from "../../components/ProjectScreens/ProjectDetailsContent";
import { useUser } from "../../context/UserContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Layers, Users, MapPin } from "lucide-react";

import axiosInstance from "../../services/api/axios";
import ProjectSettings from "./ProjectSettings";
import MarketingDocs from "./MarketingDocs";
import Documents from "./Documents";
import ChannelPartners from "./ChannelPartners";
import { fetchProjectPhases } from "../../services/projects";
import WhatsappTemplates from "./WhatsappTemplates";
import useSubscriptionFeatures from "../../hooks/useSubscriptionFeatures";
import { SUB_FEATURES, FEATURE_GATE_TOOLTIP } from "../../constants/subscriptionFeatures";

// Rendered in place of a tab's normal content when the tenant's plan
// doesn't include it. Keeps the tab itself clickable (good for upsell)
// but replaces the body with a centered message.
const PlanLockedPanel = ({ feature }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="rounded-full bg-flamingo-50 p-3 mb-3">
      <Building2 size={22} className="text-flamingo-500" />
    </div>
    <p className="text-sm font-medium text-slate-700">{FEATURE_GATE_TOOLTIP}</p>
    <p className="text-xs text-slate-500 mt-1">
      Upgrade to enable {feature.split(".").pop().replace(/_/g, " ")}.
    </p>
  </div>
);

function ProjectDetails() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [projectPhases, setProjectPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(window.location.hash.slice(1) || "projectdetails"); // Get from hash
  const [configToGo, setconfigToGo] = useState(0);
  const { userInfo } = useUser();
  const role = userInfo?.persona || "Guest";
  const { hasFeature } = useSubscriptionFeatures();

  // 1. Define Steps with Completion Status
  const [steps, setSteps] = useState([
    { name: "Plan", completed: false },
    { name: "Config", completed: false },
    { name: "Building", completed: false },
    { name: "Property", completed: false },
  ]);

  useEffect(() => {
    const handleHashChange = () => {
      setActiveTab(window.location.hash.slice(1) || "projectdetails");
    };

    window.addEventListener("hashchange", handleHashChange);

    // Initialize activeTab from hash on mount
    handleHashChange();

    // Clean up the event listener on unmount
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // useEffect to load saved step progress (from localStorage for example)
  useEffect(() => {
    if (projectId) { // Only execute if projectId is available
      const storedSteps = localStorage.getItem(`project-${projectId}-steps`);
      if (storedSteps) {
        setSteps(JSON.parse(storedSteps));
      }
    }
  }, [projectId]);//re-run effect only if the project Id changes

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/projects`, {
        params: {
          projectId: projectId,
          customerId: userInfo?.loginIdentifierDetails?.customer?.customerId,
        },
      });

      // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
      if (response?.items && Array.isArray(response.items) && response.items.length > 0) {
        setProject(response.items[0]);
      } else if (Array.isArray(response) && response.length > 0) {
        setProject(response[0]);
      } else {
        setProject(null);
      }

      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to fetch project details"
      );
      console.error("Error fetching project:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectPhasesData = async () => {
    try {
      if (projectId) {
        const phasesResponse = await fetchProjectPhases({
          projectId: projectId
        });
        console.log("Project Phases Response:", phasesResponse);

        // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
        if (phasesResponse?.items && Array.isArray(phasesResponse.items)) {
          setProjectPhases(phasesResponse.items);
        } else if (Array.isArray(phasesResponse)) {
          setProjectPhases(phasesResponse);
        } else {
          setProjectPhases([]);
        }
      }
    } catch (err) {
      console.error("Error fetching project phases:", err);
      setProjectPhases([]);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchProject();
      fetchProjectPhasesData(); // Call the new function here
    }
  }, [projectId, userInfo]);

  useEffect(() => {
    if (project) {
      setProject(project);
      // Refetch phases when project changes
      fetchProjectPhasesData();
    }
  }, [project]);

  const handleTabChange = (value, config) => {
    if (typeof value !== "string") return;
    if (config) {
      setconfigToGo(config);
    } else {
      setconfigToGo(0);
    }
    setActiveTab(value);
    window.location.hash = value;
  };
  let data = [
    {
      label: "Project details",
      value: "projectdetails",
      component: (
        <div style={{ overflowY: 'auto', scrollbarWidth: 'thin' }}>
          <ProjectDetailsContent
            project={project}
            projectId={projectId}
            projectPhases={projectPhases}
            handleTabChange={handleTabChange}
          />
        </div>
      ),
    },
    // {
    //     label: "Properties",
    //     value: "properties",
    //     component: <div style={{ overflowY: 'auto', scrollbarWidth: 'thin' }}><FlatList project={project} projectId={projectId} projectPhases={projectPhases} /></div>,
    // },
    // {
    //     label: "Status ",
    //     value: "status",
    //     component: <div style={{ overflowY: 'auto', scrollbarWidth: 'thin' }}><ProgressStatus projectId={project?.projectId} projectPhases={projectPhases} /></div>,
    // },

  ];

  // Add Marketing Documents tab only for agents
  if (role === "AGENT") {
    data.push({
      label: "Marketing Documents ",
      value: "marketing",
      component: <div style={{ overflowY: 'auto', scrollbarWidth: 'thin' }}><MarketingDocs /></div>,
    });
  }

  if (role !== "AGENT") {
    // Plan gating: tabs stay visible on Plus; their body shows an upsell
    // panel instead of the real content. RBAC has already decided the user
    // is allowed to see this page at all.
    const channelPartnersAllowed = hasFeature(SUB_FEATURES.PROJECT_CHANNEL_PARTNERS);
    const documentsAllowed = hasFeature(SUB_FEATURES.PROJECT_DOCUMENTS);
    const templatesAllowed = hasFeature(SUB_FEATURES.PROJECT_WHATSAPP_TEMPLATES);

    data = [
      ...data,
      {
        label: "Settings ",
        value: "settings",
        component: <div style={{ overflowY: 'auto', scrollbarWidth: 'thin' }}><ProjectSettings projectId={project?.projectId || project?.project_id} projectPhases={projectPhases} /></div>,
      },
      {
        label: "Channel Partners ",
        value: "channelpartners",
        component: channelPartnersAllowed ? (
          <div style={{ overflowY: 'auto', scrollbarWidth: 'thin' }}><ChannelPartners projectId={project?.projectId || project?.project_id} projectPhases={projectPhases} /></div>
        ) : (
          <PlanLockedPanel feature={SUB_FEATURES.PROJECT_CHANNEL_PARTNERS} />
        ),
      },
      {
        label: "Documents",
        value: "documents",
        component: documentsAllowed ? (
          <div style={{ overflowY: 'auto', scrollbarWidth: 'thin' }}><Documents projectId={project?.projectId || project?.project_id} projectPhases={projectPhases} /></div>
        ) : (
          <PlanLockedPanel feature={SUB_FEATURES.PROJECT_DOCUMENTS} />
        ),
      },
      {
        label: "Whatsapp Templates",
        value: "templates",
        component: templatesAllowed ? (
          <div style={{ overflowY: 'auto', scrollbarWidth: 'thin' }}><WhatsappTemplates projectId={project?.projectId || project?.project_id} projectPhases={projectPhases} /></div>
        ) : (
          <PlanLockedPanel feature={SUB_FEATURES.PROJECT_WHATSAPP_TEMPLATES} />
        ),
      },
    ];
  }

  const handleStepClick = (index) => {
    const newSteps = [...steps];
    newSteps[index].completed = !newSteps[index].completed; // Toggle completion
    setSteps(newSteps);
    //Persist (Save) completion status. localStorage Example:
    localStorage.setItem(`project-${projectId}-steps`, JSON.stringify(newSteps));
    // Alternative:  Call an API endpoint to update the completion status on the server.
    // Example:  await axiosInstance.put(`/projects/${projectId}/steps`, newSteps);
  };

  const calculateProgress = () => {
    const completedSteps = steps.filter((step) => step.completed).length;
    return (completedSteps / steps.length) * 100;
  };

  const progress = calculateProgress(); // Call the function to calculate progress

  // Only add the "Notifications" tab if the user is not an agent

  // Pull a couple of headline stats for the hero strip — same fields the
  // ProjectList card uses, with both camelCase and snake_case fallbacks.
  const projectName = project?.projectName || project?.project_name || "Project";
  const towers = project?.projectTowers || project?.towers || project?.project_towers;
  const units = project?.projectUnits || project?.units || project?.project_units;
  const developer = project?.developer || "";
  const addressLine = project?.address
    ? `${project.address.city || ""}${project.address.city && project.address.state ? ", " : ""}${project.address.state || ""}`
    : "";

  return (
    <div className="h-full space-y-4">
      {/* Always routes to the projects cards screen — not browser history.
          Inside a project page, "back" means the project list, regardless of
          which tab the user is on or how they got here. */}
      <button
        type="button"
        onClick={() => navigate("/projects")}
        className="self-start inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 font-medium"
      >
        <ArrowLeft size={16} />
        Back to Projects
      </button>

      {/* Project hero — dark slate gradient, project name + headline stats.
          Same shell language as ProjectList header / FormModal / Onboarding,
          so the page reads as part of the same product surface. */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 to-slate-600 px-5 lg:px-6 py-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-flamingo-500/20 border border-flamingo-500/30 flex items-center justify-center shrink-0">
              <Building2 size={22} className="text-flamingo-300" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-semibold text-white tracking-tight truncate">
                {projectName}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-0.5">
                {developer && (
                  <span className="text-xs text-slate-300">
                    by <span className="font-medium text-white/90">{developer}</span>
                  </span>
                )}
                {addressLine && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-300">
                    <MapPin size={11} />
                    {addressLine}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {towers ? (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-white/10 text-white border border-white/10">
                <Layers size={12} />
                {towers} {Number(towers) === 1 ? "tower" : "towers"}
              </div>
            ) : null}
            {units ? (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-flamingo-500 text-white">
                <Users size={12} />
                {units} {Number(units) === 1 ? "unit" : "units"}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Mobile view - show only the project details by default */}
      <div className="block md:hidden ">
        {/* Breadcrumb Navigation */}
        <Breadcrumb className=" pb-4 text-sm text-slate-500 font-normal">
          <Breadcrumb.Link as={Link} to="#">
            {projectName}
          </Breadcrumb.Link>
          <Breadcrumb.Separator />
          <Breadcrumb.Link as={Link} to="#">
            Project Details
          </Breadcrumb.Link>
        </Breadcrumb>
        <ProjectDetailsContent
          project={project}
          projectId={projectId}
          projectPhases={projectPhases}
        />
      </div>

      {/* Desktop view - Tab navigation */}
      <div className="hidden md:block" style={{ overflowY: 'auto', scrollbarWidth: 'thin' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          className="flex flex-col gap-4 h-full"
        >

          {/* Tab nav — bottom-border underline with a flamingo accent on the
              active tab. Sticky-style band sitting on a white card so the
              tab content below feels anchored. */}
          <div
            className="flex items-center gap-1 overflow-x-auto bg-white border-b border-slate-200 rounded-t-2xl px-3"
            style={{ scrollbarWidth: "thin" }}
          >
            {data.map(({ label, value }) => {
              const isActive = activeTab === value;
              return (
                <button
                  type="button"
                  key={value}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTabChange(value);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={`shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? "text-flamingo-700 border-flamingo-500"
                      : "text-slate-600 border-transparent hover:text-slate-900 hover:border-slate-300"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div
            data-tab-content
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {data.find((item) => item.value === activeTab)?.component || (
              <p>Select a tab to view details.</p>
            )}
          </div>
        </Tabs>
      </div>
    </div>
  );
}

export default ProjectDetails;